import React, { useState, useEffect } from 'react';
import { 
  ThumbsUp, 
  ThumbsDown, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  BookOpen,
  Beaker,
  Video,
  Globe,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { API_BASE } from '../../config/api';
import axios from 'axios';
import { cn } from '../../lib/utils';

const API = `${API_BASE}/api`;

/**
 * Admin Feedback Analytics Dashboard
 * Shows aggregated feedback data across all content types
 */
export default function FeedbackAnalytics({ token }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('thumbs_up'); // thumbs_up, thumbs_down, approval_rate
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/feedback/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching feedback analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const contentTypeIcons = {
    course: BookOpen,
    learning_lab: Beaker,
    masterclass: Video,
    external_module: Globe
  };

  const contentTypeLabels = {
    course: 'Courses',
    learning_lab: 'Learning Labs',
    masterclass: 'Masterclasses',
    external_module: 'External Modules'
  };

  const getApprovalColor = (rate) => {
    if (rate >= 80) return 'text-green-400';
    if (rate >= 60) return 'text-yellow-400';
    if (rate >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const filteredContent = analytics?.content_feedback
    ?.filter(item => selectedType === 'all' || item.content_type === selectedType)
    ?.sort((a, b) => {
      const aVal = sortBy === 'approval_rate' 
        ? (a.thumbs_up / (a.thumbs_up + a.thumbs_down) * 100 || 0)
        : a[sortBy];
      const bVal = sortBy === 'approval_rate'
        ? (b.thumbs_up / (b.thumbs_up + b.thumbs_down) * 100 || 0)
        : b[sortBy];
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-zinc-400">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No feedback data available yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-navigate-gold" />
            Learner Feedback Analytics
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Track learner sentiment across all content
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Feedback */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-sm">Total Feedback</span>
            <BarChart3 className="h-4 w-4 text-zinc-500" />
          </div>
          <p className="text-2xl font-bold text-white">{analytics.summary.total_feedback}</p>
          <p className="text-xs text-zinc-500 mt-1">
            Across {analytics.summary.content_with_feedback} items
          </p>
        </div>

        {/* Thumbs Up */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-sm">Positive Feedback</span>
            <ThumbsUp className="h-4 w-4 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-green-400">{analytics.summary.total_thumbs_up}</p>
          <div className="flex items-center gap-1 mt-1">
            <ArrowUpRight className="h-3 w-3 text-green-400" />
            <span className="text-xs text-green-400">
              {analytics.summary.overall_approval_rate}% approval
            </span>
          </div>
        </div>

        {/* Thumbs Down */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-sm">Negative Feedback</span>
            <ThumbsDown className="h-4 w-4 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-red-400">{analytics.summary.total_thumbs_down}</p>
          <p className="text-xs text-zinc-500 mt-1">
            Needs improvement
          </p>
        </div>

        {/* Top Rated */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-sm">Top Rated Content</span>
            <TrendingUp className="h-4 w-4 text-navigate-gold" />
          </div>
          <p className="text-lg font-semibold text-white truncate">
            {analytics.top_rated?.title || 'N/A'}
          </p>
          <p className="text-xs text-navigate-gold mt-1">
            {analytics.top_rated?.approval_rate || 0}% approval
          </p>
        </div>
      </div>

      {/* Breakdown by Type */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-4">Feedback by Content Type</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.by_type || {}).map(([type, data]) => {
            const Icon = contentTypeIcons[type] || BookOpen;
            const total = data.thumbs_up + data.thumbs_down;
            const rate = total > 0 ? Math.round((data.thumbs_up / total) * 100) : 0;
            
            return (
              <div 
                key={type}
                className={cn(
                  "p-3 rounded-lg border transition-colors cursor-pointer",
                  selectedType === type 
                    ? "bg-navigate-gold/10 border-navigate-gold/50" 
                    : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                )}
                onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-zinc-400" />
                  <span className="text-xs text-zinc-400">{contentTypeLabels[type]}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-green-400 text-sm font-medium">{data.thumbs_up}</span>
                    <span className="text-zinc-600">/</span>
                    <span className="text-red-400 text-sm font-medium">{data.thumbs_down}</span>
                  </div>
                  <span className={cn("text-sm font-bold", getApprovalColor(rate))}>
                    {rate}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content List */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-medium text-white">
            Content Feedback Details
            {selectedType !== 'all' && (
              <span className="ml-2 text-xs text-navigate-gold">
                ({contentTypeLabels[selectedType]})
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 rounded px-2 py-1"
            >
              <option value="thumbs_up">Most Liked</option>
              <option value="thumbs_down">Most Disliked</option>
              <option value="approval_rate">Approval Rate</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="p-1 text-zinc-400 hover:text-white"
            >
              {sortOrder === 'desc' ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {filteredContent.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            <ThumbsUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No feedback for this content type yet</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {filteredContent.slice(0, 20).map((item, idx) => {
              const Icon = contentTypeIcons[item.content_type] || BookOpen;
              const total = item.thumbs_up + item.thumbs_down;
              const rate = total > 0 ? Math.round((item.thumbs_up / total) * 100) : 0;
              
              return (
                <div key={idx} className="p-4 hover:bg-zinc-800/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-zinc-800 rounded-lg shrink-0">
                        <Icon className="h-4 w-4 text-zinc-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {item.title || item.content_id}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {contentTypeLabels[item.content_type]} • {total} responses
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6 shrink-0">
                      {/* Thumbs counts */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <ThumbsUp className="h-4 w-4 text-green-400" />
                          <span className="text-sm text-green-400 font-medium">{item.thumbs_up}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ThumbsDown className="h-4 w-4 text-red-400" />
                          <span className="text-sm text-red-400 font-medium">{item.thumbs_down}</span>
                        </div>
                      </div>
                      
                      {/* Approval Rate */}
                      <div className={cn(
                        "px-3 py-1 rounded-full text-sm font-bold",
                        rate >= 70 ? "bg-green-500/20 text-green-400" :
                        rate >= 50 ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-red-500/20 text-red-400"
                      )}>
                        {rate}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
